import { Grid } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { ReactComponent as InfoIcon } from "assets/icons/InfoIcon.svg";
import { InputField } from "components/InputField/InputField";
import ManageAchCards from "components/ManageAchCards/ManageAchCards";
import ManageCreditsCard from "components/ManageCreditsCard/ManageCreditsCard";
import RadioButtons from "components/RadioButtons/RadioButtons";
import Recaptcha from "components/Recaptcha/Recaptcha";
import StripeCard from "components/StripeCard/StripeCard";
import TranzilaCard from "components/TranzilaCard/TranzilaCard";
import { numberFormat } from "helper/numberFormat";
import FinanceService from "Lib/API/Finance";
import { emailRegex } from "utils/validation";
import React, { useEffect, useRef, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { PaymentButton, PaymentCardTitle, PaymentCardWrapper } from "./Theme";
import { Fonts, FontWeights, FontColors } from "theme/fontConfig";

export const PaymentCard = ({
  TotalForPay,
  formik,
  setError,
  error,
  PayNow,
  isLoadingPay,
  setLoadingPay,
  isStripe,
  isTranzila,
  currentPaymentMethod,
  dueAmountWithCF,
  isAuthenticated,
  recaptchaRef,
  setSelectedCard,
  selectedCard,
  setSelectedAchCard,
  selectedAchCard,
  enableCreditCardPay,
  enableAchPay,
  disabledAchPay,
  displayAchTooltip,
  disabledCreditCardPay,
  addCardStripeRef,
  publicKey,
  orgId,
  onTranzilaCharged,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isSubmittingRef = useRef(false);
  const [tranzilaCurrency, setTranzilaCurrency] = useState("ILS");
  const [tranzilaConvertedAmount, setTranzilaConvertedAmount] = useState(null);
  const isTranzilaUnauth = isTranzila && !isAuthenticated;
  const isBillingFilled = !isTranzilaUnauth || (
    !!formik?.values?.accountInfo?.first_name?.trim() &&
    !!formik?.values?.accountInfo?.last_name?.trim() &&
    emailRegex.test(formik?.values?.accountInfo?.primary_email_address || '') &&
    (formik?.values?.cardsDetails?.zip?.trim() || '').length >= 4
  );

  useEffect(() => {
    if (!isTranzila || !isAuthenticated || tranzilaCurrency !== "USD" || !(+dueAmountWithCF > 0)) {
      setTranzilaConvertedAmount(null);
      return;
    }
    FinanceService.tranzilaHandshakePublic({
      paymentMethodId: currentPaymentMethod?._id,
      orgId,
      amount: dueAmountWithCF,
      currency: "USD",
    }).then(res => setTranzilaConvertedAmount(res?.convertedAmount ?? null))
      .catch(() => setTranzilaConvertedAmount(null));
  }, [tranzilaCurrency, dueAmountWithCF, isTranzila, isAuthenticated, orgId, currentPaymentMethod?._id]);

  const handleExpiryChange = (text) => {
    formik.setFieldValue("cardsDetails.expiry", text);
    if (text.length > 2) {
      if (text[2] !== "/") {
        formik.setFieldValue(
          "cardsDetails.expiry",
          text.substring(0, 2) + "/" + text.substring(2, 4)
        );
      }
    }
  };
  useEffect(() => {
    if (isAuthenticated) return setError({ value: false, message: "" });
    const emailValidation = emailRegex.test(formik?.values?.accountInfo?.email);
    if (
      !formik?.values?.accountInfo?.first_name ||
      !formik?.values?.accountInfo?.last_name ||
      !formik?.values?.accountInfo?.email ||
      (!isStripe &&
        !formik?.values?.cardsDetails?.zip &&
        formik?.values?.activePayment === "credit-card")
    ) {
      setError({ value: true, message: "Please fill all mandatory fields" });
      return;
    }

    if (!emailValidation) {
      setError({
        value: false,
        message: "Please enter valid email",
        type: "emailError",
      });
      return;
    }

    if (
      formik?.values?.activePayment === "credit-card" &&
      !isStripe &&
      (!formik?.values?.cardsDetails?.card_number ||
        !formik.values?.cardsDetails?.expiry ||
        !formik.values?.cardsDetails?.cvc ||
        formik.values?.cardsDetails?.cvc?.length < 3)
    ) {
      setError({
        value: true,
        message: "All card details fields are required",
      });
      return;
    } else if (
      formik?.values?.activePayment === "credit-card" &&
      !isStripe &&
      formik?.values?.cardsDetails?.card_number?.length < 14
    ) {
      setError({
        value: false,
        message: "The card number must contain 14 number",
        type: "CardNumberError",
      });
      return;
    } else if (
      formik?.values?.activePayment === "credit-card" &&
      !isStripe &&
      formik.values?.cardsDetails?.cvc?.length < 3
    ) {
      setError({
        value: false,
        message: "The CVC should be 3 or 4 digits",
        type: "CardNumberError",
      });
      return;
    } else {
      setError({ value: false, message: "" });
      return;
    }
  }, [formik?.values]);

  const handlePayment = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      await PayNow(isStripe || false);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const paymentTabs = [
    ...(enableCreditCardPay
      ? [
          {
            label: "Pay with credit card",
            value: "credit-card",
            disabled: disabledCreditCardPay,
          },
        ]
      : []),
    ...(enableAchPay
      ? [
          {
            label: "Pay with ACH",
            value: "ach",
            disabled: disabledAchPay,
            showTooltip: displayAchTooltip,
            tooltip: displayAchTooltip,
            tooltipPlacement: "top",
          },
        ]
      : []),
  ];

  return (
    <PaymentCardWrapper style={isMobile ? { padding: "16px" } : undefined}>
      <PaymentCardTitle style={isMobile ? { marginBottom: "12px" } : undefined}>
        <span>Payment</span>
      </PaymentCardTitle>
      <Grid
        container
        spacing={isMobile ? 2 : 3}
      >
        {paymentTabs?.length > 1 && (
          <Grid
            item
            xs={12}
            style={{
              marginBottom: "-15px",
            }}
          >
            <RadioButtons
              value={formik?.values?.activePayment}
              name={"activePayment"}
              row={true}
              onChange={(value) => {
                formik.setFieldValue("activePayment", value);
              }}
              defaultValue={"credit-card"}
              size={"small"}
              options={paymentTabs}
            />
          </Grid>
        )}
        {formik?.values?.activePayment === "credit-card" ? (
          <>
            {isAuthenticated ? (
              <Grid item xs={12} className="d-flex align-items-center">
                <ManageCreditsCard
                  isStripe={isStripe}
                  isTranzila={isTranzila}
                  currentPaymentMethod={currentPaymentMethod}
                  setSelectedCard={setSelectedCard}
                  selectedCard={selectedCard}
                  publicKey={publicKey}
                  onCardCurrencyChange={(cur) => setTranzilaCurrency(cur || "ILS")}
                />
              </Grid>
            ) : isTranzila ? (
              <Grid item xs={12}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  {['ILS', 'USD'].map((cur) => (
                    <button key={cur} type="button"
                      onClick={() => { setTranzilaCurrency(cur); setTranzilaConvertedAmount(null); }}
                      style={{ padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
                        border: tranzilaCurrency === cur ? '2px solid #2555EF' : '1px solid #e2e8f0',
                        background: tranzilaCurrency === cur ? '#EEF2FF' : '#fff',
                        color: tranzilaCurrency === cur ? '#2555EF' : '#333',
                        fontWeight: tranzilaCurrency === cur ? FontWeights.SemiBold : FontWeights.Regular }}>
                      {cur === 'ILS' ? '₪ ILS' : '$ USD'}
                    </button>
                  ))}
                  {tranzilaCurrency === 'USD' && tranzilaConvertedAmount && (
                    <span style={{ color: FontColors.Gray555555, fontSize: Fonts.GeneralAmountFont.fontSize }}>≈ ${tranzilaConvertedAmount.toFixed(2)} USD</span>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  {!isBillingFilled && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '4px' }}>
                      <p style={{ color: FontColors.RedE53E3E, textAlign: 'center', padding: '16px', fontWeight: FontWeights.Medium, margin: 0 }}>
                        Please fill in all billing details above to continue with payment.
                      </p>
                    </div>
                  )}
                  <TranzilaCard
                    onSaveCard={() => {}}
                    onCharged={onTranzilaCharged}
                    loading={isLoadingPay}
                    setLoading={setLoadingPay}
                    saveCard={false}
                    currency={tranzilaCurrency}
                    amount={+TotalForPay || 0}
                    terminalName={currentPaymentMethod?.paymentGateway?.keys?.terminal_name}
                    paymentMethodId={currentPaymentMethod?._id}
                    orgId={orgId}
                    onHandshake={({ convertedAmount }) => setTranzilaConvertedAmount(convertedAmount)}
                  />
                </div>
              </Grid>
            ) : isStripe ? (
              <Grid item xs={12}>
                <StripeCard
                  loading={isLoadingPay}
                  setLoading={setLoadingPay}
                  saveCard={false}
                  ref={addCardStripeRef}
                  publicKey={publicKey}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={12} md={6}>
                  <InputField
                    placeholder="XXXX XXXX XXXX XXXX"
                    label="Card Number"
                    styleWrapper={{ width: "100%" }}
                    style={{
                      width: "100%",
                      background:
                        TotalForPay === 0 && "var(--Gray-10, #F8F8F9)",
                    }}
                    isOptional={false}
                    labelStyle={{
                      color:
                        TotalForPay === 0
                          ? "var(--Gray-100, #B5B5C3)"
                          : "var(--Dark-Blue-100, #002E5C)",
                      ...Fonts.GeneralLabelFont,
                    }}
                    value={formik.values?.cardsDetails.card_number}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    handleChange={(e) => {
                      formik.setFieldValue(
                        "cardsDetails.card_number",
                        e?.target?.value
                      );
                    }}
                    minLength="16"
                    maxLength="16"
                    isGray={TotalForPay === 0}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InputField
                    placeholder="MM/YY"
                    label="Expire Date"
                    styleWrapper={{ width: "100%" }}
                    style={{
                      width: "100%",
                      background:
                        TotalForPay === 0 && "var(--Gray-10, #F8F8F9)",
                    }}
                    isOptional={false}
                    labelStyle={{
                      color:
                        TotalForPay === 0
                          ? "var(--Gray-100, #B5B5C3)"
                          : "var(--Dark-Blue-100, #002E5C)",
                      ...Fonts.GeneralLabelFont,
                    }}
                    isGray={TotalForPay === 0}
                    value={formik.values?.cardsDetails.expiry}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    handleChange={(e) => handleExpiryChange(e?.target?.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <InputField
                    placeholder="3-4 digits"
                    label="CVC"
                    minLength="4"
                    maxLength="4"
                    styleWrapper={{ width: "100%" }}
                    style={{
                      width: "100%",
                      background:
                        TotalForPay === 0 && "var(--Gray-10, #F8F8F9)",
                    }}
                    isOptional={false}
                    labelStyle={{
                      color:
                        TotalForPay === 0
                          ? "var(--Gray-100, #B5B5C3)"
                          : "var(--Dark-Blue-100, #002E5C)",
                      ...Fonts.GeneralLabelFont,
                    }}
                    value={formik.values?.cardsDetails.cvc}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    handleChange={(e) => {
                      formik.setFieldValue(
                        "cardsDetails.cvc",
                        e?.target?.value
                      );
                    }}
                    isGray={TotalForPay === 0}
                  />
                </Grid>
              </>
            )}
          </>
        ) : formik?.values?.activePayment === "ach" ? (
          <Grid item xs={12} className="d-flex align-items-center">
            <ManageAchCards
              setSelectedAchCard={setSelectedAchCard}
              selectedAchCard={selectedAchCard}
            />
          </Grid>
        ) : null}

        {!isAuthenticated && !isTranzilaUnauth && (
          <Grid item xs={12} className="d-flex align-items-center">
            <Recaptcha recaptchaRef={recaptchaRef} />
          </Grid>
        )}
        {!isTranzilaUnauth && <Grid item xs={12}>
          <PaymentButton
            isDisabled={
              error?.value ||
              TotalForPay === 0 ||
              !formik?.values?.activePayment ||
              isLoadingPay
            }
          >
            <button
              type="button"
              disabled={
                error?.value ||
                TotalForPay === 0 ||
                !formik?.values?.activePayment ||
                isLoadingPay
              }
              onClick={() => {
                if (
                  !error?.value &&
                  TotalForPay > 0 &&
                  formik?.values?.activePayment &&
                  !isLoadingPay
                ) {
                  handlePayment();
                }
              }}
            >
              {isLoadingPay ? (
                <ClipLoader
                  color={"#ffffff"}
                  loading={isLoadingPay}
                  size={32}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              ) : TotalForPay > 0 ? (
                isTranzila && tranzilaCurrency === "USD" && tranzilaConvertedAmount
                  ? `PAY $${tranzilaConvertedAmount.toFixed(2)} USD`
                  : `PAY ${numberFormat({ number: +dueAmountWithCF, decimal: 2, style: "currency" })}`
              ) : (
                "PAY"
              )}
            </button>
          </PaymentButton>
        </Grid>}
      </Grid>
    </PaymentCardWrapper>
  );
};
